import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion } from "framer-motion";
import FastMarquee from "react-fast-marquee";
import Lenis from "lenis";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Instagram,
  Youtube,
} from "lucide-react";
import { WaitlistModal } from "@/components/WaitlistModal";

const IMG = {
  heroMobile:
    "https://customer-assets-jai6qajn.emergentagent.net/job_zone-of-genius/artifacts/8ezc3s2v_SOYT%20Insta%20Beitrag.png",
  heroDesktop:
    "https://customer-assets-eiarnc6j.emergentagent.net/job_663e7332-83c8-4777-8b9e-7887ca2bdeec/artifacts/bgfq51fg_SOYT%20Banner.png",
  chess:
    "https://customer-assets-jai6qajn.emergentagent.net/job_zone-of-genius/artifacts/1h8fcbaf_Schach%20Backgammon%20Querformat.png",
  fallen:
    "https://customer-assets-jai6qajn.emergentagent.net/job_zone-of-genius/artifacts/q9y7cqgw_umgefallene%20Figuren.png",
  luisaSun:
    "https://customer-assets-jai6qajn.emergentagent.net/job_zone-of-genius/artifacts/93vr47x0_Luisa%20Sonnenbrille.png",
  luisaProv:
    "https://customer-assets-jai6qajn.emergentagent.net/job_zone-of-genius/artifacts/s1az5m2s_Luisa%20provokativer.png",
};

const QUOTES = [
  "„Dank Dir und dem tollen Workshop habe ich tatsächlich meinen Traumjob gefunden — und mich sogar selbstständig gemacht. Etwas, von dem ich nie gedacht hätte, dass ich es jemals sagen würde. Erst durch Dich habe ich begonnen, meine Glaubenssätze zu hinterfragen — und bin jetzt so happy, wie ich es noch nie war.“",
  "„Letztes Jahr hatte ich ein Seminar bei Dir, das wirklich mein Leben verändert hat. Mit neuer Kraft und Mut habe ich einen noch verantwortungsvolleren und spannenderen Job angenommen. Ich bin sehr erfolgreich — und mache genau das, was ich immer machen wollte.“",
  "„Luisas Programm und Luisa selbst haben meine Erwartungen mehr als übertroffen. Ich habe so viel über mich gelernt, was mein Selbstvertrauen und Selbstbewusstsein gestärkt hat, wie ich es nicht für möglich gehalten habe. Danke Luisa!“",
  "„LEUTE, ICH HAB DEN JOB!!! Inklusive meiner Gehaltsvorstellung! Die innere Klarheit hat mir geholfen, die Gelegenheit überhaupt zu erkennen.“",
  "„Ich habe direkt am nächsten Tag eine Einladung zum Vorstellungsgespräch erhalten! Danke fürs Beweise schaffen, dass sich die Mühe lohnt — und ich trotz Teilzeit und 3-fach-Mama relevant für den Arbeitsmarkt bin. Oder gerade deshalb.“",
];

const FEATURED_SHOTS = [
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_b9b4a337-83e4-4dce-9689-1a273e2b4a7e/artifacts/8qf4foir_IMG_5707.jpeg",
    alt: "Teilnehmerin beschreibt ihre größte Erkenntnis: spielen, um zu gewinnen — und geht einen großen Schritt",
  },
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_b9b4a337-83e4-4dce-9689-1a273e2b4a7e/artifacts/si6lqsfr_IMG_5699.jpeg",
    alt: "Teilnehmerin hat sich neu in ihren Mann verliebt und setzt klare Grenzen im Job",
  },
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_b9b4a337-83e4-4dce-9689-1a273e2b4a7e/artifacts/fup1vprt_IMG_5766.jpeg",
    alt: "Teilnehmerin spricht berufliche Bedenken offen an — statt das Wochenende zu overthinken",
  },
];

const MORE_SHOTS = [
  "https://customer-assets-jai6qajn.emergentagent.net/job_b9b4a337-83e4-4dce-9689-1a273e2b4a7e/artifacts/n9nw497y_IMG_5709.jpeg",
  "https://customer-assets-jai6qajn.emergentagent.net/job_b9b4a337-83e4-4dce-9689-1a273e2b4a7e/artifacts/z45o4geg_IMG_5718.jpeg",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_insider-games/artifacts/4fiklqcl_IMG_5729.jpeg",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_insider-games/artifacts/cl9mm8xg_IMG_5727.jpeg",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_insider-games/artifacts/3bvtzhrj_IMG_5648.jpeg",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_insider-games/artifacts/nu9wfyrt_IMG_5652.jpeg",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_insider-games/artifacts/68p7bg60_IMG_5748.jpeg",
];

const SPIELPLAN = [
  ["Spielstart", "Noch unbekannt"],
  ["Spielregeln", "Werden vorab nicht verraten … Genau das macht es spannend."],
  ["Spielformat", "Aktivierende Impulse über Telegram, direkt auf Dein Smartphone."],
  ["Spieldauer", "Mindestens 14 Tage"],
  ["Spielcharakter", "Ko-Kreation statt Konkurrenz. Wir gewinnen gemeinsam, oder gar nicht."],
];

const SpotifyIcon = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.5 17.3c-.22.36-.68.47-1.04.25-2.85-1.74-6.44-2.14-10.66-1.17-.4.09-.81-.16-.9-.56-.09-.4.16-.81.56-.9 4.57-1.04 8.5-.59 11.66 1.35.36.21.47.67.25 1.03zm1.47-3.27c-.28.45-.86.59-1.31.31-3.26-2.01-8.24-2.59-12.1-1.42-.5.15-1.03-.13-1.18-.63-.15-.5.13-1.03.63-1.18 4.42-1.34 9.93-.69 13.65 1.61.45.28.59.86.31 1.31zm.13-3.41C15.24 8.4 8.82 8.16 5.16 9.3c-.6.18-1.24-.16-1.42-.76-.18-.6.16-1.24.76-1.42 4.23-1.28 11.25-1.04 15.68 1.46.54.32.72 1.02.4 1.56-.32.53-1.02.72-1.56.4z" />
  </svg>
);

const ApplePodcastIcon = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

const Reveal = ({ children, className = "", delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-3 font-body text-[11px] font-medium uppercase tracking-[0.34em] text-[#A39790] sm:text-xs">
    <span className="h-px w-8 bg-gold opacity-70" />
    {children}
  </span>
);

const CtaButton = ({ children, onClick, testId, className = "" }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`group relative inline-flex items-center justify-center gap-3 bg-gold px-9 py-4 font-heading text-sm font-bold uppercase tracking-wider text-[#1a1207] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:glow-gold sm:text-base ${className}`}
  >
    {children}
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
  </button>
);

const Hero = ({ onJoin }) => (
  <section
    className="relative overflow-hidden pb-20 pt-16 lg:pb-24 lg:pt-20"
    data-testid="hero-section"
  >
    <div className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[#FFF2B0] opacity-[0.08] blur-[120px]" />
    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,242,176,0.08),transparent_55%)] opacity-40" />
    <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="mb-8 flex justify-center"
      >
        <Eyebrow>Eine Limonata Coaching Experience</Eyebrow>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.1 }}
        className="relative overflow-hidden border border-gold"
      >
        <img
          alt="Success on Your Terms — Erfüllt im Job ohne Abstriche · by Luisa Bergholz"
          className="mx-auto block w-full max-w-md object-cover sm:hidden"
          src={IMG.heroMobile}
        />
        <img
          alt="Success on Your Terms — Erfüllt im Job ohne Abstriche · by Luisa Bergholz"
          className="hidden w-full object-cover sm:block"
          src={IMG.heroDesktop}
        />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.25 }}
        className="mx-auto mt-12 max-w-2xl text-center"
      >
        <p className="text-lg leading-relaxed text-gold sm:text-xl">
          Erfolgreich im Job — und trotzdem spürst Du, dass da mehr sein muss.
        </p>
        <p className="mt-4 text-base leading-relaxed text-[#A39790] sm:text-lg">
          Eine Experience, in der Du aus dem alten Spiel von Arbeit und Karriere
          aussteigst — und Dein eigenes kreierst.
        </p>
        <div className="mt-9 flex justify-center">
          <CtaButton onClick={onJoin} testId="hero-cta">
            I'm in
          </CtaButton>
        </div>
      </motion.div>
    </div>
  </section>
);

const MARQUEE_ITEMS = [
  "Success on Your Terms",
  "Stop trying to change the rules",
  "Change the Game",
];

const Marquee = () => (
  <div
    className="border-y border-gold bg-[#0A0807] py-6"
    data-testid="marquee"
  >
    <FastMarquee speed={45} gradient gradientColor="#0A0807" gradientWidth={120}>
      {MARQUEE_ITEMS.map((text) => (
        <span
          key={text}
          className="mx-10 inline-flex items-center gap-10 font-accent text-2xl font-light italic text-[#A39790] sm:text-3xl"
        >
          {text}
          <span className="not-italic text-gold">✦</span>
        </span>
      ))}
    </FastMarquee>
  </div>
);

const ZonesSection = () => (
  <section
    className="relative bg-[#0A0807] py-28 lg:py-36"
    data-testid="zones-section"
  >
    <div className="mx-auto max-w-3xl px-6 lg:px-10">
      <Reveal>
        <h2 className="font-heading text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
          Du bist gut in Deinem Job. Sehr gut sogar. Und genau das ist{" "}
          <span className="text-gold">das Problem</span>.
        </h2>
        <p className="mt-8 text-base leading-relaxed text-[#A39790] sm:text-lg">
          Von außen bekommst Du ständig gespiegelt, dass Du großartige Arbeit
          leistest — und trotzdem fühlst Du Dich innerlich leer. Aber aufgeben,
          was Du Dir aufgebaut hast? Vor allem, wenn Du doch offensichtlich so
          gut darin bist?
        </p>
        <div className="mt-6 space-y-1.5 border-l-2 border-gold pl-6">
          <p className="text-base italic text-[#D8CFC8] sm:text-lg">
            Völlig absurd, daran etwas zu ändern.
          </p>
          <p className="text-base italic text-[#D8CFC8] sm:text-lg">
            Viel zu riskant, all das aufs Spiel zu setzen.
          </p>
          <p className="font-heading text-xl font-semibold text-gold sm:text-2xl">
            Oder?
          </p>
        </div>
        <p className="mt-6 text-base leading-relaxed text-[#A39790] sm:text-lg">
          Diese Sätze klingen vernünftig. So wie man das halt macht, wenn man
          Karriere machen möchte.
        </p>
        <p className="mt-6 font-accent text-xl italic leading-relaxed text-gold sm:text-2xl">
          Aber wer genau hat eigentlich entschieden, dass Erfolg so aussehen
          muss? Dass Du Dich damit abfinden musst, dass Du nicht alles haben
          kannst — Erfolg UND Erfüllung?
        </p>
      </Reveal>
    </div>
  </section>
);

const ManifestoItem = ({ children, variant = "body" }) => {
  const styles = {
    lead: "text-xl leading-relaxed text-[#F4F0EB] sm:text-2xl",
    body: "text-base leading-relaxed text-[#A39790] sm:text-lg",
    statement:
      "font-heading text-2xl font-semibold leading-relaxed text-[#F4F0EB] sm:text-3xl",
    accent: "font-accent text-xl italic leading-relaxed text-gold sm:text-2xl",
  };
  return (
    <Reveal>
      <div className="relative">
        <span className="absolute -left-8 top-2.5 h-2 w-2 -translate-x-1/2 rounded-full bg-[#FFF2B0]" />
        <p className={styles[variant]}>{children}</p>
      </div>
    </Reveal>
  );
};

const ManifestoSection = () => (
  <section
    className="relative mx-auto max-w-3xl px-6 py-28 lg:py-36"
    data-testid="manifesto-section"
  >
    <Reveal>
      <Eyebrow>Das alte Spiel</Eyebrow>
      <h2 className="mt-6 font-heading text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
        Die alten Regeln des <span className="text-gold">Erfolgs</span>
      </h2>
    </Reveal>
    <div className="relative mt-14 space-y-9 pl-8">
      <span className="absolute bottom-3 left-0 top-3 w-px bg-gradient-to-b from-[#EDD98F]/60 via-[#EDD98F]/15 to-transparent" />
      <ManifestoItem variant="lead">
        Wir haben gelernt, wie ein <span className="italic">gutes</span> Leben
        und eine <span className="italic">richtige</span> Karriere auszusehen
        hat. Wie Erfolg definiert wird.
      </ManifestoItem>
      <ManifestoItem>
        Nach wessen Regeln im Arbeitsmarkt gespielt wird.
      </ManifestoItem>
      <Reveal>
        <div className="-ml-8 my-2 overflow-hidden border border-gold">
          <img
            alt="Eine Hand zieht eine Schachfigur auf einem Backgammon-Brett"
            className="w-full object-cover"
            src={IMG.chess}
          />
        </div>
      </Reveal>
      <ManifestoItem>
        Es ist ein Spiel, geprägt von Gegeneinander, Misstrauen und Mangel.
      </ManifestoItem>
      <ManifestoItem>
        Die bösen Arbeitgeber, die einen nur ausbeuten wollen. Die faulen
        Arbeitnehmer, die nicht wirklich arbeiten wollen, aber ständig
        Forderungen stellen.
      </ManifestoItem>
      <ManifestoItem>
        Wir glauben, wir müssten über unsere Grenzen gehen, um erfolgreich zu
        sein. Tragen Hosenanzüge und Blazer als Rüstung, um unseren wahren Kern
        zu schützen. Und verlagern das Leben auf die Bereiche außerhalb der
        Arbeit.
      </ManifestoItem>
      <ManifestoItem variant="statement">
        Kein Wunder, dass wir Arbeit als anstrengend und seelenlos empfinden.
      </ManifestoItem>
      <ManifestoItem>
        Die meisten von uns verbringen Jahre, wenn nicht ihr gesamtes
        Berufsleben damit, entweder nach diesen Regeln zu spielen und sich dabei
        selbst zu verlieren. Oder dagegen anzukämpfen und sich aufzureiben an
        einem System, das sie nicht ändern können.
      </ManifestoItem>
      <ManifestoItem variant="accent">
        Das Problem: Beides hält Dich im selben Spiel gefangen.
      </ManifestoItem>
      <ManifestoItem>
        In einem Spiel, in dem es keine echten Gewinner gibt. In dem es scheint,
        als müsstest Du wählen zwischen Karriere, Erfolg und Reichtum auf der
        einen Seite — und Sinn, Erfüllung und liebevollen Beziehungen auf der
        anderen.
      </ManifestoItem>
      <ManifestoItem>
        Du spürst schon länger diese Sehnsucht in Dir, dass es doch auch einen
        anderen Weg geben muss. Denn dieses Spiel entspricht Dir nicht.
      </ManifestoItem>
    </div>
  </section>
);

const GameSection = () => (
  <section className="relative overflow-hidden" data-testid="game-section">
    <img
      alt=""
      className="absolute inset-0 h-[125%] w-full object-cover opacity-95"
      src={IMG.fallen}
    />
    <div className="absolute inset-0 bg-[#0A0807]/30" />
    <div className="absolute inset-0 bg-gradient-to-b from-[#15100E] via-transparent to-[#15100E]" />
    <div className="relative mx-auto max-w-4xl px-6 py-32 text-center lg:py-44">
      <Reveal>
        <p className="font-accent text-xl italic text-[#A39790] sm:text-2xl">
          Anstatt zu versuchen, die Spielregeln zu verändern …
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-8 font-heading text-5xl font-black leading-[0.95] tracking-tight sm:text-7xl lg:text-8xl">
          <span className="text-[#F4F0EB]">Let's change</span>
          <br />
          <span className="text-gold">the GAME.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mx-auto mt-10 max-w-xl text-base leading-relaxed text-[#A39790] sm:text-lg">
          Nicht härter dagegen ankämpfen. Nicht noch mehr anpassen. Sondern das
          Spielfeld verlassen, das sowieso nie Deines war.
        </p>
      </Reveal>
    </div>
  </section>
);

const NewGameSection = () => (
  <section
    className="relative mx-auto max-w-3xl px-6 py-28 lg:py-36"
    data-testid="newgame-section"
  >
    <Reveal>
      <Eyebrow>Das neue Spiel</Eyebrow>
      <h2 className="mt-6 font-heading text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
        Es geht nicht darum, das alte Spiel{" "}
        <span className="italic">besser</span> zu spielen.
        <span className="mt-3 block text-gold">
          Es geht darum, Dein eigenes Spiel zu kreieren.
        </span>
      </h2>
    </Reveal>
    <Reveal>
      <p className="mt-12 font-heading text-2xl font-semibold text-[#F4F0EB] sm:text-3xl">
        Success on Your Terms ist für Dich, wenn Du eine Arbeit möchtest …
      </p>
      <ul className="mt-6 space-y-5">
        {[
          "die sich vor allem von innen großartig anfühlt — ganz egal, wie andere darüber denken.",
          "die Dir und Deinem Wesen entspricht — und Dir hilft, noch mehr ganz Du zu werden und immer wieder über Dich hinauszuwachsen.",
          "in der Du aussteigst aus den alten Narrativen — weil Du nicht länger negativ über „das Management“, „die Kunden“ oder „die Mitarbeitenden“ denken willst, sondern echte Beziehungen von Mensch zu Mensch leben möchtest, unabhängig von der jeweiligen Rolle.",
        ].map((item) => (
          <li
            key={item}
            className="flex gap-4 text-base leading-relaxed text-[#A39790] sm:text-lg"
          >
            <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#FFF2B0]" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </Reveal>
    <Reveal>
      <p className="mt-14 font-heading text-2xl font-semibold text-[#F4F0EB] sm:text-3xl">
        Stell Dir mal vor …
      </p>
      <ul className="mt-6 space-y-5 border-l border-[#EDD98F]/30 pl-6">
        {[
          "Du gestaltest Deinen Arbeitstag so, wie er zu Dir passt — Zeiten, Ort, Tempo — und kreierst dabei gleichzeitig das beste Ergebnis für alle.",
          "Du schaffst echten Mehrwert, statt einfach nur Stunden abzusitzen.",
          "Deine Energietanks werden während der Arbeit aufgeladen, statt leergesaugt zu werden.",
          "Du wirkst Seite an Seite mit anderen statt gegeneinander — egal ob Du gerade Mitarbeiterin, Führungskraft oder Kundin bist.",
          "Dein Arbeitgeber ist Dein größter Fürsprecher (nach Dir selbst) und will Dich gewinnen sehen.",
        ].map((item) => (
          <li
            key={item}
            className="text-base leading-relaxed text-[#A39790] sm:text-lg"
          >
            <span className="text-gold">… </span>
            {item}
          </li>
        ))}
      </ul>
    </Reveal>
    <Reveal>
      <p className="mt-14 text-base leading-relaxed text-[#A39790] sm:text-lg">
        Das ist für die Frau, die nicht länger nur reagieren, sondern aktiv
        (mit)gestalten möchte, wie ihre Arbeit und ihre Zukunft aussehen können.
        Egal, ob angestellt oder selbstständig.
      </p>
      <p className="mt-8 border-l-2 border-gold pl-6 font-accent text-xl italic leading-relaxed text-gold sm:text-2xl">
        Success on Your Terms heißt: Tun, was Du liebst. UND richtig gut dafür
        bezahlt werden. UND wirklich Mehrwert für andere schaffen.
      </p>
    </Reveal>
  </section>
);

const IncludedSection = ({ onJoin }) => (
  <section
    className="mx-auto max-w-3xl px-6 py-28 lg:py-36"
    data-testid="included-section"
  >
    <Reveal>
      <Eyebrow>Was Dich erwartet</Eyebrow>
      <h2 className="mt-6 font-heading text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
        Die Spiele werden <span className="text-gold">bald wieder</span>{" "}
        eröffnet.
      </h2>
    </Reveal>
    <Reveal>
      <div className="relative mt-12 border border-gold bg-gradient-to-b from-[#231B18] to-[#15100E] p-8 sm:p-12">
        <span className="pointer-events-none absolute left-3 top-3 h-4 w-4 border-l border-t border-gold" />
        <span className="pointer-events-none absolute right-3 top-3 h-4 w-4 border-r border-t border-gold" />
        <span className="pointer-events-none absolute bottom-3 left-3 h-4 w-4 border-b border-l border-gold" />
        <span className="pointer-events-none absolute bottom-3 right-3 h-4 w-4 border-b border-r border-gold" />
        <p className="text-center font-heading text-xs uppercase tracking-[0.4em] text-gold">
          Spielanleitung
        </p>
        <div className="mx-auto mt-3 h-px w-16 bg-gold opacity-40" />
        <dl className="mt-8 divide-y divide-[rgba(255,242,176,0.12)]">
          {SPIELPLAN.map(([term, desc]) => (
            <div
              key={term}
              className="flex flex-col gap-1.5 py-5 sm:flex-row sm:items-baseline sm:gap-8"
            >
              <dt className="shrink-0 font-heading text-xs uppercase tracking-[0.28em] text-gold sm:w-44">
                {term}
              </dt>
              <dd className="text-lg leading-relaxed text-[#F4F0EB]">{desc}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Reveal>
    <Reveal>
      <p className="mt-8 text-center font-accent text-lg italic leading-relaxed text-[#D8CFC8] sm:text-xl">
        Wenn Du auf der Insiderliste stehst, erfährst Du als Erste, wenn die
        Spiele eröffnet werden.
      </p>
      <div className="mt-8 flex justify-center">
        <CtaButton onClick={onJoin} testId="included-cta">
          Auf die Insiderliste
        </CtaButton>
      </div>
    </Reveal>
  </section>
);

const SHOT_SLIDES = [
  [FEATURED_SHOTS[0]],
  [FEATURED_SHOTS[1]],
  [FEATURED_SHOTS[2]],
  [{ src: MORE_SHOTS[0], alt: "Screenshot aus der SOYT-Runde" }],
  [{ src: MORE_SHOTS[1], alt: "Screenshot aus der SOYT-Runde" }],
  [
    { src: MORE_SHOTS[2], alt: "Screenshot aus der SOYT-Runde" },
    { src: MORE_SHOTS[3], alt: "Screenshot aus der SOYT-Runde" },
  ],
  [
    { src: MORE_SHOTS[4], alt: "Screenshot aus der SOYT-Runde" },
    { src: MORE_SHOTS[5], alt: "Screenshot aus der SOYT-Runde" },
  ],
  [{ src: MORE_SHOTS[6], alt: "Screenshot aus der SOYT-Runde" }],
];

const ScreenshotsSection = () => {
  const trackRef = useRef(null);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);

  const update = () => {
    const el = trackRef.current;
    if (!el) return;
    setPages(Math.max(1, Math.round(el.scrollWidth / el.clientWidth)));
    setPage(Math.round(el.scrollLeft / el.clientWidth));
  };

  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollByPage = (dir) =>
    trackRef.current?.scrollBy({
      left: dir * trackRef.current.clientWidth,
      behavior: "smooth",
    });

  const goTo = (i) =>
    trackRef.current?.scrollTo({
      left: i * trackRef.current.clientWidth,
      behavior: "smooth",
    });

  return (
    <section
      className="relative bg-[#0A0807] py-28 lg:py-36"
      data-testid="screenshots-section"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal className="max-w-3xl">
          <Eyebrow>Stimmen aus dem Spiel</Eyebrow>
          <h2 className="mt-6 font-heading text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
            Was sich schon während des Spiels für bisherige{" "}
            <span className="text-gold">Spielerinnen verändert hat</span>
          </h2>
        </Reveal>
        <Reveal className="mt-14">
          <div
            ref={trackRef}
            onScroll={update}
            className="no-scrollbar flex snap-x snap-mandatory items-start gap-5 overflow-x-auto"
            data-testid="shots-track"
          >
            {SHOT_SLIDES.map((group, gi) => (
              <div
                key={gi}
                className="flex w-[80%] shrink-0 snap-start flex-col gap-5 sm:w-[55%] lg:w-[calc(33.333%-14px)]"
              >
                {group.map((shot, si) => (
                  <figure
                    key={shot.src}
                    className="border border-gold bg-[#15100E] p-3"
                  >
                    <img
                      src={shot.src}
                      alt={shot.alt}
                      loading="lazy"
                      className="w-full object-contain object-top"
                      data-testid={`shot-image-${gi}-${si}`}
                    />
                  </figure>
                ))}
              </div>
            ))}
          </div>
          <div className="mt-8 flex items-center justify-between">
            <div className="flex gap-2">
              {Array.from({ length: pages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`Screenshots Seite ${i + 1}`}
                  data-testid={`shots-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    page === i ? "w-8 bg-gold" : "w-3 bg-[#3a2e28]"
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => scrollByPage(-1)}
                aria-label="Zurück"
                data-testid="shots-prev"
                className="flex h-11 w-11 items-center justify-center border border-gold text-[#F4F0EB] transition-colors hover:bg-gold hover:text-[#1a1207]"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => scrollByPage(1)}
                aria-label="Weiter"
                data-testid="shots-next"
                className="flex h-11 w-11 items-center justify-center border border-gold text-[#F4F0EB] transition-colors hover:bg-gold hover:text-[#1a1207]"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const TestimonialsSection = () => {
  const trackRef = useRef(null);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);

  const update = () => {
    const el = trackRef.current;
    if (!el) return;
    setPages(Math.max(1, Math.round(el.scrollWidth / el.clientWidth)));
    setPage(Math.round(el.scrollLeft / el.clientWidth));
  };

  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollByPage = (dir) =>
    trackRef.current?.scrollBy({
      left: dir * trackRef.current.clientWidth,
      behavior: "smooth",
    });

  const goTo = (i) =>
    trackRef.current?.scrollTo({
      left: i * trackRef.current.clientWidth,
      behavior: "smooth",
    });

  return (
    <section
      className="relative bg-[#0A0807] py-28 lg:py-36"
      data-testid="testimonials-section"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <Eyebrow>Kundinnenerfolge</Eyebrow>
          <h2 className="mt-6 font-heading text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
            Sie haben das Spielfeld bereits{" "}
            <span className="text-gold">gewechselt</span>.
          </h2>
        </Reveal>
        <Reveal className="mt-14">
          <div
            ref={trackRef}
            onScroll={update}
            className="no-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto"
            data-testid="testimonial-track"
          >
            {QUOTES.map((quote, i) => (
              <figure
                key={i}
                className="flex w-[86%] shrink-0 snap-start flex-col border border-gold bg-[#15100E] p-7 sm:w-[60%] sm:p-8 lg:w-[calc(33.333%-14px)]"
              >
                <blockquote
                  className="flex-1 font-accent text-lg font-light italic leading-relaxed text-[#F4F0EB]"
                  data-testid={`testimonial-quote-${i}`}
                >
                  {quote}
                </blockquote>
              </figure>
            ))}
          </div>
          <div className="mt-8 flex items-center justify-between">
            <div className="flex gap-2">
              {Array.from({ length: pages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`Stimme ${i + 1}`}
                  data-testid={`testimonial-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    page === i ? "w-8 bg-gold" : "w-3 bg-[#3a2e28]"
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => scrollByPage(-1)}
                aria-label="Zurück"
                data-testid="testimonial-prev"
                className="flex h-11 w-11 items-center justify-center border border-gold text-[#F4F0EB] transition-colors hover:bg-gold hover:text-[#1a1207]"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => scrollByPage(1)}
                aria-label="Weiter"
                data-testid="testimonial-next"
                className="flex h-11 w-11 items-center justify-center border border-gold text-[#F4F0EB] transition-colors hover:bg-gold hover:text-[#1a1207]"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const BrandBand = () => (
  <section
    className="relative overflow-hidden bg-[#0A0807]"
    data-testid="brand-band"
  >
    <div className="relative h-[420px] overflow-hidden sm:h-[520px] lg:hidden">
      <img
        alt="Luisa Bergholz"
        className="absolute inset-0 h-[116%] w-full object-cover object-[center_25%]"
        src={IMG.luisaSun}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0807] via-[#0A0807]/20 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0807]/70 via-transparent to-transparent" />
      <div className="relative mx-auto flex h-full max-w-7xl items-center px-6">
        <Reveal>
          <p className="max-w-md font-accent text-2xl italic leading-snug text-[#F4F0EB] sm:text-4xl">
            Es ist kein Erfolg, wenn Du dafür andere Bereiche Deines Lebens
            opfern musst.
          </p>
          <p className="mt-4 font-heading text-lg font-semibold uppercase tracking-widest text-gold">
            Success on Your Terms. Ohne Abstriche.
          </p>
        </Reveal>
      </div>
    </div>
    <div className="mx-auto hidden max-w-7xl items-stretch gap-12 px-10 py-20 lg:flex">
      <div className="flex flex-1 items-center">
        <Reveal>
          <p className="max-w-lg font-accent text-4xl italic leading-snug text-[#F4F0EB]">
            Es ist kein Erfolg, wenn Du dafür andere Bereiche Deines Lebens
            opfern musst.
          </p>
          <p className="mt-5 font-heading text-lg font-semibold uppercase tracking-widest text-gold">
            Success on Your Terms. Ohne Abstriche.
          </p>
        </Reveal>
      </div>
      <div className="relative w-[380px] shrink-0 overflow-hidden border border-gold">
        <img
          alt="Luisa Bergholz"
          className="h-[480px] w-full object-cover object-[center_25%]"
          src={IMG.luisaSun}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0807]/40 to-transparent" />
      </div>
    </div>
  </section>
);

const WaitlistSection = ({ onJoin }) => (
  <section
    className="mx-auto max-w-3xl px-6 py-28 lg:py-36"
    data-testid="waitlist-section"
  >
    <Reveal>
      <div className="relative overflow-hidden border border-gold bg-gradient-to-b from-[#231B18] to-[#0A0807] p-10 text-center sm:p-14">
        <div className="pointer-events-none absolute -top-20 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-[#E5C158] opacity-10 blur-[90px]" />
        <span className="inline-flex items-center gap-2 border border-gold px-4 py-1.5 font-body text-[11px] font-medium uppercase tracking-[0.28em] text-[#A39790]">
          <Sparkles className="h-3.5 w-3.5 text-gold" />
          Die Spiele kommen zurück
        </span>
        <h2 className="mt-8 font-heading text-3xl font-bold sm:text-4xl">
          Setz Dich hier auf die{" "}
          <span className="text-gold">Insiderliste</span>
        </h2>
        <div className="my-8 h-px w-full bg-gold opacity-30" />
        <div>
          <CtaButton
            onClick={onJoin}
            testId="waitlist-cta"
            className="w-full sm:w-auto"
          >
            I'm in
          </CtaButton>
        </div>
        <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-[#A39790] sm:text-base">
          Du verpflichtest Dich an dieser Stelle zu nichts. Du wirst als Erste
          informiert, sobald die Spiele eröffnet werden.
        </p>
      </div>
    </Reveal>
  </section>
);

const CoachSection = () => (
  <section
    className="mx-auto max-w-7xl px-6 py-24 lg:px-10"
    data-testid="coach-section"
  >
    <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
      <Reveal>
        <div className="arch relative mx-auto max-w-sm overflow-hidden border border-gold">
          <img
            alt="Luisa Bergholz"
            className="h-[540px] w-full object-cover object-top"
            src={IMG.luisaProv}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#15100E] to-transparent" />
        </div>
      </Reveal>
      <Reveal>
        <Eyebrow>Deine Spielleiterin</Eyebrow>
        <h2 className="mt-6 font-heading text-4xl font-bold tracking-tight sm:text-5xl">
          Luisa <span className="text-gold">Bergholz</span>
        </h2>
        <p className="mt-5 font-accent text-lg italic leading-relaxed text-gold sm:text-xl">
          Arbeitspsychologin, Expertin für berufliche Neuorientierung,
          Projektorin, Dolce-Vita-Genießerin.
        </p>
        <p className="mt-6 text-base leading-relaxed text-[#A39790] sm:text-lg">
          Ich glaube nicht, dass wir uns zwischen Erfolg und Erfüllung
          entscheiden müssen.
        </p>
        <p className="mt-4 text-base leading-relaxed text-[#A39790] sm:text-lg">
          Ich habe selbst lange nach den alten Regeln gespielt, war Meisterin
          darin, sie brav zu befolgen — selbst als Selbstständige noch. Bis ich
          anfing, mich zu fragen: Wer hat eigentlich gesagt, dass Arbeit so
          aussehen muss?
        </p>
        <p className="mt-4 text-base leading-relaxed text-[#A39790] sm:text-lg">
          Inzwischen lebe ich ein Leben, in dem sich meine Arbeit auf natürliche
          Weise einfügt. Genieße die Arbeit mit meinen Kundinnen genauso sehr
          wie ein Treffen mit Freundinnen. Und halte immer wieder in mittlerweile
          ganz „normalen“ Alltagsmomenten inne und blicke mich staunend um, dass
          DAS tatsächlich mein ArbeitsLeben ist.
        </p>
        <p className="mt-4 text-base leading-relaxed text-[#A39790] sm:text-lg">
          „Success on Your Terms" katapultiert Dich aus dem alten Spiel — hin zu
          einer Arbeit, die sich wirklich nach Dir anfühlt.
        </p>
        <p className="mt-6 font-accent text-2xl italic text-gold sm:text-3xl">
          Are you ready to Change the Game?
        </p>
      </Reveal>
    </div>
  </section>
);

const FOOTER_LINKS = [
  ["Impressum", "https://luisabergholz.com/impressum"],
  ["Datenschutz", "https://luisabergholz.com/datenschutzerklaerung"],
  ["AGB", "https://luisabergholz.com/agb"],
  ["Vertrag widerrufen", "https://luisabergholz.com/widerrufsformular"],
  ["Kontakt", "https://luisabergholz.com/kontakt"],
];

const Footer = () => (
  <footer
    className="border-t border-[rgba(229,193,88,0.25)] bg-[#0A0807]"
    data-testid="footer"
  >
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
      <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
        <div className="flex items-center gap-5">
          <span className="font-accent italic text-[#A39790]">
            by Luisa Bergholz
          </span>
          <a
            href="https://www.instagram.com/luisa.bergholz/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            data-testid="footer-instagram"
            className="text-[#A39790] transition-colors hover:text-gold"
          >
            <Instagram className="h-4 w-4" />
          </a>
          <a
            href="https://www.youtube.com/@luisa.bergholz"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="YouTube"
            data-testid="footer-youtube"
            className="text-[#A39790] transition-colors hover:text-gold"
          >
            <Youtube className="h-4 w-4" />
          </a>
        </div>
        <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#A39790]">
          {FOOTER_LINKS.map(([label, href]) => (
            <a
              key={href}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className="transition-colors hover:text-gold"
            >
              {label}
            </a>
          ))}
        </nav>
      </div>
      <p className="mt-8 text-xs text-[#756A63]">
        © 2026 Luisa Bergholz. Alle Rechte vorbehalten.
      </p>
    </div>
  </footer>
);

const ConfirmPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App noise">
      <main className="relative min-h-screen bg-[#15100E]">
        <section
          className="relative overflow-hidden py-20 lg:py-28"
          data-testid="confirm-section"
        >
          <div className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[#FFF2B0] opacity-[0.08] blur-[120px]" />
          <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
            <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
              <Reveal>
                <Eyebrow>Insiderliste</Eyebrow>
                <h1 className="mt-6 font-heading text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                  Yesss, Du stehst auf der{" "}
                  <span className="text-gold">Insiderliste!</span>
                </h1>
                <p className="mt-8 text-base leading-relaxed text-[#A39790] sm:text-lg">
                  Du hast Dich erfolgreich in die Insiderliste für Success on
                  Your Terms eingetragen und erhältst damit als Erste Infos
                  dazu, wenn die Spiele beginnen.
                </p>
                <div className="mt-8 lg:hidden">
                  <div className="arch relative mx-auto max-w-[220px] overflow-hidden border border-gold">
                    <img
                      src={IMG.luisaProv}
                      alt="Luisa Bergholz"
                      className="h-[280px] w-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#15100E] to-transparent" />
                  </div>
                </div>
                <p className="mt-10 font-accent text-2xl italic leading-snug text-gold sm:text-3xl">
                  Du willst am liebsten gleich losstarten?
                </p>
                <p className="mt-3 font-heading text-xl font-semibold text-[#F4F0EB] sm:text-2xl">
                  I got you!
                </p>
                <p className="mt-6 text-base leading-relaxed text-[#A39790] sm:text-lg">
                  Im SELBSTNEUERFINDERIN Podcast warten hunderte Folgen rund um
                  das Thema „einen Job finden, der sich wirklich nach Dir
                  anfühlt" auf Dich.
                </p>
              </Reveal>
              <Reveal delay={0.15} className="hidden lg:block">
                <div className="arch relative mx-auto max-w-sm overflow-hidden border border-gold">
                  <img
                    src={IMG.luisaProv}
                    alt="Luisa Bergholz"
                    className="h-[520px] w-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#15100E] to-transparent" />
                </div>
              </Reveal>
            </div>
          </div>
        </section>
        <section className="mx-auto max-w-4xl px-6 pb-24" data-testid="confirm-podcast">
          <Reveal>
            <div className="overflow-hidden border border-gold">
              <div className="aspect-video w-full">
                <iframe
                  src="https://www.youtube.com/embed/wCy7oO_iZc0"
                  title="SELBSTNEUERFINDERIN Podcast Folge"
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="podcast-iframe"
                />
              </div>
            </div>
            <div className="mt-6 flex flex-col items-stretch justify-center gap-4 sm:flex-row sm:items-center">
              <a
                href="https://open.spotify.com/show/62zpVyG79oV06aJg0QVGwD"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="podcast-spotify"
                className="inline-flex items-center justify-center gap-3 border border-gold px-6 py-3.5 font-heading text-sm font-semibold text-[#F4F0EB] transition-[transform,background-color,color] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-[#1a1207]"
              >
                <SpotifyIcon className="h-4 w-4" />
                Folge auf Spotify anhören
              </a>
              <a
                href="https://podcasts.apple.com/podcast/id/1759373675"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="podcast-apple"
                className="inline-flex items-center justify-center gap-3 border border-gold px-6 py-3.5 font-heading text-sm font-semibold text-[#F4F0EB] transition-[transform,background-color,color] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-[#1a1207]"
              >
                <ApplePodcastIcon className="h-4 w-4" />
                Folge auf Apple Podcast anhören
              </a>
            </div>
          </Reveal>
        </section>
        <section
          className="mx-auto max-w-3xl px-6 pb-28 text-center"
          data-testid="confirm-connect"
        >
          <Reveal>
            <h2 className="font-heading text-3xl font-bold sm:text-4xl">
              Let's <span className="text-gold">connect!</span>
            </h2>
            <p className="mt-4 text-base text-[#A39790] sm:text-lg">
              Folge mir auf Instagram
            </p>
            <div className="mt-8 flex justify-center">
              <a
                href="https://www.instagram.com/luisa.bergholz/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="confirm-instagram"
                className="group inline-flex items-center gap-3 border border-gold px-8 py-4 font-heading text-sm font-bold uppercase tracking-wider text-[#F4F0EB] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-[#1a1207] hover:glow-gold"
              >
                <Instagram className="h-4 w-4" />
                @luisa.bergholz
              </a>
            </div>
          </Reveal>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const Landing = () => {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const openWaitlist = () => setWaitlistOpen(true);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.1 });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App noise">
      <main className="relative min-h-screen bg-[#15100E]">
        <Hero onJoin={openWaitlist} />
        <Marquee />
        <ZonesSection />
        <ManifestoSection />
        <GameSection />
        <NewGameSection />
        <TestimonialsSection />
        <IncludedSection onJoin={openWaitlist} />
        <ScreenshotsSection />
        <BrandBand />
        <WaitlistSection onJoin={openWaitlist} />
        <CoachSection />
      </main>
      <Footer />
      <WaitlistModal open={waitlistOpen} onClose={() => setWaitlistOpen(false)} />
    </div>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/bestaetigt" element={<ConfirmPage />} />
      </Routes>
    </BrowserRouter>
  );
}
